import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    BringzzTopBar,
    BringzzIcon
} from "@bringzz/components";
import BringzzLogo from 'assets/BringzzLogo.png';
import { useTheme } from 'context/ThemeContext';
import { useNavigation } from '../../context/NavigationContext';
import useRequest from 'hooks/useRequest';
import { useTranslation } from 'react-i18next';
import useHomepagePolling from 'hooks/useHomepagePolling';
// OrderStatusCard removed - using ProgressOverlay instead
import { USER_ROLES, ORDER_STATUS } from 'utils/constants';

const ShopperHomepage = () => {
    const { navigate } = useNavigation();
    const { isMobile } = useTheme();
    const { sendRequest } = useRequest();
    const { t } = useTranslation();
    const [map, setMap] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userLocation, setUserLocation] = useState(null);
    const [pharmacies, setPharmacies] = useState([]);
    const [activeOrderData, setActiveOrderData] = useState(null);
    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
    const mapRef = useRef(null);
    const watchId = useRef(null);
    const currentMarker = useRef(null);
    const pharmacyMarkers = useRef([]);

    // Use the new polling manager for active orders and notifications
    useHomepagePolling({
        enabled: true,
        activeOrders: {
            enabled: true,
            interval: 30000, // Poll every 30 seconds
            immediate: true,
            onSuccess: (orderData) => {
                setActiveOrderData(orderData);
            }
        },
        notifications: {
            enabled: true,
            interval: 45000, // Poll every 45 seconds
            immediate: true,
            onSuccess: (data) => {
                setHasUnreadNotifications(data.hasUnread);
            }
        }
    });

    // Initialize the map
    const initializeMap = useCallback((initialLocation = null) => {
        if (!window.mapkit || !mapRef.current) {
            console.log('Map initialization failed - missing mapkit or mapRef');
            return;
        }

        try {
            const mapToken = process.env.REACT_APP_APPLE_MAP_KEY;

            window.mapkit.init({
                authorizationCallback: (done) => {
                    done(mapToken);
                },
            });

            const newMap = new window.mapkit.Map(mapRef.current, {
                mapType: window.mapkit.Map.MapTypes.Standard,
                showsCompass: window.mapkit.FeatureVisibility.Hidden,
                showsMapTypeControl: false,
                showsScale: window.mapkit.FeatureVisibility.Hidden,
                isZoomEnabled: true,
                isScrollEnabled: true,
                showsZoomControl: true,
            });

            if (initialLocation) {
                const coordinate = new window.mapkit.Coordinate(
                    initialLocation.latitude,
                    initialLocation.longitude
                );
                const region = new window.mapkit.CoordinateRegion(
                    coordinate,
                    new window.mapkit.CoordinateSpan(0.01, 0.01)
                );
                newMap.region = region;
            }

            setMap(newMap);
            setLoading(false);
        } catch (error) {
            console.error('Error initializing map:', error);
            setLoading(false);
        }
    }, []);

    // Update user location marker on the map
    const updateUserLocationMarker = useCallback((location) => {
        if (!map || !window.mapkit) return;

        try {
            // Remove existing marker if any
            if (currentMarker.current) {
                map.removeAnnotation(currentMarker.current);
            }

            // Create new marker for user location
            const coordinate = new window.mapkit.Coordinate(
                location.latitude,
                location.longitude
            );

            const marker = new window.mapkit.MarkerAnnotation(coordinate, {
                title: "You are here",
                subtitle: `Accuracy: ${Math.round(location.accuracy || 0)}m`,
                color: "#7B6AFF", // magic-lilac color
                glyphText: "📍",
                displayPriority: 2000, // Higher than destination markers
                animates: false, // Disable animation to prevent disappearing
                selected: false
            });

            map.addAnnotation(marker);
            currentMarker.current = marker;

            // Center map on user location
            const region = new window.mapkit.CoordinateRegion(
                coordinate,
                new window.mapkit.CoordinateSpan(0.01, 0.01)
            );
            map.region = region;
        } catch (error) {
            console.error('Error updating location marker:', error);
        }
    }, [map]);

    // Handle location button click
    const handleMyLocation = useCallback(async () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const location = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy
                    };
                    setUserLocation(location);
                    updateUserLocationMarker(location);
                },
                (error) => {
                    console.error('Error getting location:', error);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        }
    }, [updateUserLocationMarker]);

    // Function to add pharmacy markers to the map
    const addPharmacyMarkers = useCallback((pharmaciesData) => {
        if (!map || !window.mapkit || !pharmaciesData) return;

        try {
            // Remove existing pharmacy markers
            pharmacyMarkers.current.forEach(marker => {
                map.removeAnnotation(marker);
            });
            pharmacyMarkers.current = [];

            // Add new pharmacy markers
            Object.values(pharmaciesData).forEach(pharmacy => {
                if (pharmacy.address && pharmacy.address.latitude && pharmacy.address.longitude) {
                    const coordinate = new window.mapkit.Coordinate(
                        parseFloat(pharmacy.address.latitude),
                        parseFloat(pharmacy.address.longitude)
                    );

                    const marker = new window.mapkit.MarkerAnnotation(coordinate, {
                        title: pharmacy.name,
                        subtitle: `${pharmacy.address.street_name} ${pharmacy.address.house_number}, ${pharmacy.address.postal_code} ${pharmacy.address.city_name}`,
                        color: "#FF6B6B", // Red color for pharmacies
                        glyphText: "💊",
                        displayPriority: 1000,
                        animates: false,
                        selected: false
                    });

                    map.addAnnotation(marker);
                    pharmacyMarkers.current.push(marker);
                }
            });
        } catch (error) {
            console.error('Error adding pharmacy markers:', error);
        }
    }, [map]);

    // Fetch local pharmacies
    const fetchLocalPharmacies = useCallback(async () => {
        try {
            const response = await sendRequest({
                method: 'GET',
                url: '/module/pharmacy/getLocalPharmacies'
            });

            if (response.status === 'success' && response.data) {
                setPharmacies(response.data);
                addPharmacyMarkers(response.data);
            }
        } catch (error) {
            console.error('Error fetching local pharmacies:', error);
        }
    }, [sendRequest, addPharmacyMarkers]);

    // Initialize map and get user location on mount
    useEffect(() => {
        const checkPermissionAndInit = async () => {
            try {
                // Try to get user location first
                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            const location = {
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude,
                                accuracy: position.coords.accuracy
                            };
                            setUserLocation(location);
                            initializeMap(location);

                            // Start watching location
                            watchId.current = navigator.geolocation.watchPosition(
                                (pos) => {
                                    const newLocation = {
                                        latitude: pos.coords.latitude,
                                        longitude: pos.coords.longitude,
                                        accuracy: pos.coords.accuracy
                                    };
                                    setUserLocation(newLocation);
                                    updateUserLocationMarker(newLocation);
                                },
                                (error) => {
                                    console.error('Watch position error:', error);
                                },
                                {
                                    enableHighAccuracy: true,
                                    maximumAge: 2000
                                }
                            );
                        },
                        (error) => {
                            console.error('Error getting initial location:', error);
                            // Initialize map without location
                            initializeMap();
                        },
                        {
                            enableHighAccuracy: true,
                            timeout: 10000,
                            maximumAge: 0
                        }
                    );
                } else {
                    // Initialize map without location
                    initializeMap();
                }
            } catch (error) {
                console.error('Error during initialization:', error);
                initializeMap();
            }
        };

        checkPermissionAndInit();

        // Cleanup
        return () => {
            if (watchId.current) {
                navigator.geolocation.clearWatch(watchId.current);
            }
            // Remove pharmacy markers before destroying map
            if (map && pharmacyMarkers.current.length > 0) {
                pharmacyMarkers.current.forEach(marker => {
                    map.removeAnnotation(marker);
                });
            }
            if (map) {
                map.destroy();
            }
        };
    }, []);

    // Fetch pharmacies when map is ready
    useEffect(() => {
        if (map) {
            fetchLocalPharmacies();
        }
    }, [map, fetchLocalPharmacies]);

    // Update pharmacy markers when pharmacies data changes
    useEffect(() => {
        if (pharmacies.length > 0 && map) {
            addPharmacyMarkers(pharmacies);
        }
    }, [pharmacies, map, addPharmacyMarkers]);

    // Update marker when location changes
    useEffect(() => {
        if (userLocation && map) {
            updateUserLocationMarker(userLocation);
        }
    }, [userLocation, map, updateUserLocationMarker]);

    return (
        <div className="flex flex-col h-screen">
            <BringzzTopBar
                sections={[
                    { name: 'logo', alignment: 'start', value: BringzzLogo },
                    {
                        name: 'notification',
                        alignment: 'end',
                        value: hasUnreadNotifications,
                        onClick: () => navigate('/notifications')
                    }
                ]}
            />

            {/* Active Order Status Card - Shows when there's an active order */}
            {/* Active order tracking is handled by ProgressOverlay component */}

            <div className="relative flex-1 w-full">
                {/* Map container */}
                <div
                    ref={mapRef}
                    className="absolute inset-0 w-full h-full"
                />

                {/* Map Controls - My location button */}
                <div className="absolute bottom-10 right-3 flex flex-col gap-2 z-10">
                    <button
                        className="bg-white w-10 h-10 rounded-full flex items-center justify-center shadow-md hover:bg-gray-50 transition-colors"
                        onClick={handleMyLocation}
                    >
                        <BringzzIcon
                            icon="IconGeoLocation"
                            folder="HealthIcons"
                            size="20"
                            className="p-2"
                        />
                    </button>
                </div>

                {loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-20">
                        <div className="text-gray-600">Loading map...</div>
                    </div>
                )}
            </div>

            {isMobile && <div className='h-24'></div>}
        </div>
    );
};

export default ShopperHomepage;